import React from 'react';

import AlertCircleOutlineIcon from 'mdi-react/AlertCircleOutlineIcon';
import CheckCircleOutlineIcon from 'mdi-react/CheckCircleOutlineIcon';
import CloseCircleOutlineIcon from 'mdi-react/CloseCircleOutlineIcon';
import InfoCircleOutlineIcon from 'mdi-react/InfoCircleOutlineIcon';

import { Box, Flex, Icon, Text } from '@chakra-ui/react';

export type CompactCalloutVariant = 'info' | 'success' | 'warning' | 'error';

type CompactCalloutProps = {
  variant: CompactCalloutVariant;
  title: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  testId?: string;
  mb?: number;
  mt?: number;
};

type VariantStyles = {
  icon: React.ElementType;
  iconColor: string;
  iconBackground: string;
};

const variantStyles: Record<CompactCalloutVariant, VariantStyles> = {
  info: {
    icon: InfoCircleOutlineIcon,
    iconColor: '#2563EB',
    iconBackground: '#EFF6FF',
  },
  success: {
    icon: CheckCircleOutlineIcon,
    iconColor: '#15803D',
    iconBackground: '#ECFDF3',
  },
  warning: {
    icon: AlertCircleOutlineIcon,
    iconColor: '#B45309',
    iconBackground: '#FFFBEB',
  },
  error: {
    icon: CloseCircleOutlineIcon,
    iconColor: '#B91C1C',
    iconBackground: '#FEF2F2',
  },
};

const CompactCallout: React.FC<CompactCalloutProps> = ({
  variant,
  title,
  children,
  action,
  testId,
  mb,
  mt,
}) => {
  const styles = variantStyles[variant];

  return (
    <Box data-testid={testId} mb={mb} mt={mt}>
      <Flex alignItems="flex-start" gap={3}>
        <Flex
          w={7}
          h={7}
          borderRadius="full"
          alignItems="center"
          justifyContent="center"
          bg={styles.iconBackground}
          mt="2px"
          flexShrink={0}
        >
          <Icon as={styles.icon} color={styles.iconColor} w={4} h={4} />
        </Flex>

        <Box flex="1">
          <Text fontWeight="semibold" fontSize="lg" color="gray.800">
            {title}
          </Text>

          {children && (
            <Box mt={1.5} fontSize="lg" color="gray.700">
              {children}
            </Box>
          )}
        </Box>

        {action && <Box flexShrink={0}>{action}</Box>}
      </Flex>
    </Box>
  );
};

export default CompactCallout;
